import type { CSSProperties } from 'react';
import type { OptGroup } from './fieldGroups';
import { groupsHave } from './fieldGroups';

/**
 * THE field picker: one `<select>` arranged the way the form is built.
 *
 * Every place in the admin that asks "which question?" renders this — Logic,
 * Access, the workflow steps, the follow-up wizard, the document parts, the
 * builder's own cross-references — so two questions that share a label are
 * told apart the same way everywhere: grouped under Section › Card, each
 * printed as `Label (key)`, a composite's parts as `Label › Part`. The groups
 * come from `fieldGroups.ts`; this only draws them.
 *
 * A value no group knows (a question since deleted) is still shown, flagged,
 * rather than silently snapping to the first option.
 */
export default function FieldPicker({
  value, onChange, groups, placeholder = '— pick a question —', allowClear = true, disabledValues,
  className, style, id, ariaLabel, title, disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  groups: OptGroup[];
  /** the empty option's text */
  placeholder?: string;
  /** false: no empty option once something is chosen (a rule needs a field) */
  allowClear?: boolean;
  /** options shown but not choosable — already used by another row */
  disabledValues?: Set<string>;
  className?: string;
  style?: CSSProperties;
  id?: string;
  ariaLabel?: string;
  title?: string;
  disabled?: boolean;
}) {
  const known = !value || groupsHave(groups, value);
  return (
    <select
      className={className} style={style} id={id} aria-label={ariaLabel} title={title} disabled={disabled}
      value={value} onChange={(e) => onChange(e.target.value)}
    >
      {(allowClear || !value) && <option value="">{placeholder}</option>}
      {!known && <option value={value}>⚠ {value} (not on this form)</option>}
      {groups.filter((g) => g.opts.length).map((g, gi) => (
        <optgroup key={`${gi}:${g.label}`} label={g.label}>
          {g.opts.map((o) => (
            <option key={o.v} value={o.v} disabled={!!disabledValues?.has(o.v) && o.v !== value}>{o.l}</option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}
